import React, { useRef } from 'react';
import useSWRInfinite from 'swr/infinite';
import { useMatchMutate } from '../../../utils/swr/swr';
import { Design, MarketingPaginatedResponse } from '../../../providers/api/dtos';
import { searchDesigns } from '../../../providers/api/marketing/marketingProvider';

export const SEARCH_DESIGNS_RID = 'search-designs';

const INTERVAL_MS = 2000;
const MAX_REVALIDATIONS = 5;

interface UseSearchDesignsInfiniteResult {
  data: Design[],
  isLoading: boolean,
  isLoadingMore: boolean,
  hasMore: boolean,
  error: Error | undefined,
  loadMore: () => void,
  mutate: () => void,
}

export const useSearchDesignsInfinite = (query?: string, autoRefreshForDesignId?: string): UseSearchDesignsInfiniteResult => {
  const matchMutate = useMatchMutate();
  const countRef = useRef(0);

  const getKey = (pageIndex: number, previousPage: MarketingPaginatedResponse<Design> | null) => {
    if (previousPage && !previousPage.continuation) return null;

    const continuation = previousPage?.continuation ?? null;
    return [SEARCH_DESIGNS_RID, query ?? '', continuation];
  };

  const result = useSWRInfinite(
    getKey,
    ([, , continuation]) => searchDesigns(query, { continuation: continuation ?? undefined }),
    {
      refreshInterval: (data) => {
        const content = data?.[0]?.content;
        if (!autoRefreshForDesignId || content?.some((value) => value.id === autoRefreshForDesignId) || countRef.current >= MAX_REVALIDATIONS) {
          countRef.current = 0;
          return 0; // stop polling
        }

        countRef.current += 1;
        return INTERVAL_MS;
      },
      revalidateOnFocus: false,
      revalidateFirstPage: true,
      revalidateAll: true,
    },
  );

  const allItems = result.data?.flatMap((page) => page.content) ?? [];
  const isLoadingMore = result.isValidating && (result.data?.length ?? 0) > 0;
  const lastPage = result.data?.at(-1);

  const loadMore = React.useCallback(() => {
    if (lastPage?.continuation) {
      result.setSize((s) => s + 1);
    }
  }, [lastPage?.continuation, result]);

  return {
    data: allItems,
    isLoading: result.isLoading,
    isLoadingMore,
    error: result.error,
    hasMore: !!lastPage?.continuation,
    loadMore,
    mutate: () => matchMutate(SEARCH_DESIGNS_RID),
  };
};
